/* Typed HTTP client. Base URL swaps to the real Spring Boot API via
   VITE_API_BASE; defaults to '/api' (browser → MSW worker). */
import { type ListQuery, type Page, toSearchParams } from './contracts'

const BASE = ((import.meta.env.VITE_API_BASE as string | undefined) ?? '/api').replace(/\/+$/, '')

/* Токен держится здесь, а не читается из хранилища при каждом запросе: так
   единственное место, знающее про вход, — слой auth, который его сюда кладёт. */
let bearer: string | null = null

export function setAuthToken(token: string | null): void {
  bearer = token
}

/* Активный проект. У сотрудника, работающего на двух стройках, в токене
   несколько проектов, и который из них сейчас — решает интерфейс. Значение
   уходит заголовком в каждый запрос; сервер принимает его ТОЛЬКО если проект
   есть в токене, поэтому изоляция остаётся токенной, а не параметром запроса. */
let activeProject: string | null = null

export function setActiveProject(project: string | null): void {
  activeProject = project
}

/* Симметрично токену: слой auth регистрирует, что делать, когда сервер токен
   отверг. Без этого 401 только гасил повторы запроса, а пользователь оставался
   на экране с общей строкой ошибки и без способа войти заново. */
let onUnauthorized: (() => void) | null = null

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler
}

export interface ProblemField {
  field: string
  code: string
  message: string
}

/**
 * Ошибка API в форме RFC 7807 (`application/problem+json`) плюс машинный `code`.
 * По коду клиент выбирает сообщение; `title` — запасной вариант для кода,
 * которого клиент не знает.
 */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly title: string
  readonly detail?: string
  readonly errors?: ProblemField[]

  constructor(status: number, code: string, title: string, detail?: string, errors?: ProblemField[]) {
    super(detail ? `${title}: ${detail}` : title)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.title = title
    this.detail = detail
    this.errors = errors
  }
}

/** 401 — это не сбой данных, а требование войти заново; UI обязан их различать. */
export class UnauthorizedError extends ApiError {
  constructor(title = 'Требуется вход', detail?: string) {
    super(401, 'auth.unauthenticated', title, detail)
    this.name = 'UnauthorizedError'
  }
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

interface RequestOptions {
  signal?: AbortSignal
  /* Ключ идемпотентности. Обязателен для любой команды: без него повтор
     запроса после обрыва связи выпускает второй трансмиттал, а двойной клик —
     второе замечание. Сервер отбивает команду без ключа. */
  idempotencyKey?: string
}

async function toError(method: string, path: string, res: Response): Promise<ApiError> {
  // Тело читается всегда: раньше оно отбрасывалось, и доменная причина отказа
  // («нет ответа подрядчика», «маршрут не завершён») до пользователя не доходила.
  let body: Record<string, unknown> = {}
  try {
    body = (await res.json()) as Record<string, unknown>
  } catch {
    /* не problem+json — обойдёмся статусом */
  }
  const code = typeof body.code === 'string' ? body.code : `http.${res.status}`
  const title = typeof body.title === 'string' ? body.title : `${method} ${path} → ${res.status}`
  const detail = typeof body.detail === 'string' ? body.detail : undefined
  const errors = Array.isArray(body.errors) ? (body.errors as ProblemField[]) : undefined

  if (res.status === 401) return new UnauthorizedError(title, detail)
  return new ApiError(res.status, code, title, detail, errors)
}

export async function request<T>(
  method: Method,
  path: string,
  body?: unknown,
  opts?: RequestOptions,
): Promise<T> {
  const headers: Record<string, string> = {}
  if (bearer) headers.Authorization = `Bearer ${bearer}`
  if (activeProject) headers['X-Project'] = activeProject
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  if (opts?.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey

  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal: opts?.signal,
  })

  if (!res.ok) {
    const error = await toError(method, path, res)
    /* Вход запрашивается только если токен был и сервер его отверг. Запрос без
       токена — это гонка при старте, и просить войти в ответ на неё значит
       уводить в бесконечный редирект. */
    if (error instanceof UnauthorizedError && headers.Authorization) onUnauthorized?.()
    throw error
  }
  return (await res.json()) as T
}

/**
 * Отправка файла на разбор сервером (заполненный бланк CRS).
 *
 * <p>Единственное место, где байты идут ЧЕРЕЗ приложение: файлы документов
 * грузятся мимо него, прямо в хранилище по подписанной ссылке. Бланк — не
 * документ, а форма, раскладку которой знает сервер: он её и печатал.
 *
 * <p>Content-Type не выставляется руками: границу multipart назначает сам
 * браузер, и подставленный заголовок сделал бы тело нечитаемым.
 */
export async function uploadForm<T>(path: string, file: File, opts?: RequestOptions): Promise<T> {
  const headers: Record<string, string> = {}
  if (bearer) headers.Authorization = `Bearer ${bearer}`
  if (activeProject) headers['X-Project'] = activeProject
  if (opts?.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey

  const form = new FormData()
  form.append('file', file)
  const res = await fetch(`${BASE}${path}`, { method: 'POST', headers, body: form, signal: opts?.signal })
  if (!res.ok) {
    const error = await toError('POST', path, res)
    if (error instanceof UnauthorizedError && headers.Authorization) onUnauthorized?.()
    throw error
  }
  return (await res.json()) as T
}

/**
 * Файл, собранный сервером (печатная форма трансмиттала).
 *
 * <p>Обычной ссылкой его не взять: переход браузера по адресу не несёт
 * заголовка с токеном, а API закрыт правилом «запрещено по умолчанию» — та же
 * причина, по которой ссылка на скачивание документа приходит телом ответа.
 * Имя файла берётся из заголовка: его знает сервер, а не экран.
 */
export async function requestFile(path: string, opts?: RequestOptions): Promise<{ blob: Blob; fileName: string }> {
  const headers: Record<string, string> = {}
  if (bearer) headers.Authorization = `Bearer ${bearer}`
  if (activeProject) headers['X-Project'] = activeProject

  const res = await fetch(`${BASE}${path}`, { method: 'GET', headers, signal: opts?.signal })
  if (!res.ok) {
    const error = await toError('GET', path, res)
    if (error instanceof UnauthorizedError && headers.Authorization) onUnauthorized?.()
    throw error
  }
  const disposition = res.headers.get('Content-Disposition') ?? ''
  const match = /filename="?([^"]+)"?/.exec(disposition)
  return { blob: await res.blob(), fileName: match?.[1] ?? path.split('/').pop() ?? 'file' }
}

/** Отдать собранный сервером файл браузеру. Ссылка живёт ровно один клик. */
export async function saveFile(path: string): Promise<string> {
  const { blob, fileName } = await requestFile(path)
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  link.click()
  URL.revokeObjectURL(url)
  return fileName
}

/**
 * Отметить выгрузку в журнале.
 *
 * <p>Файлы собирает браузер — байты не идут через приложение, — и следа от
 * этого не оставалось: по скачанному документу ответ есть, а по выгрузке всего
 * реестра со сроками по контракту не было ни кто, ни когда, ни с каким
 * отбором. Тот же провал стоял на выгрузке самого журнала аудита.
 *
 * <p>Сбой отметки не отменяет уже собранный файл: он у человека на диске, и
 * притворяться, что выгрузки не было, бессмысленно.
 */
export function noteExport(what: string, scope?: string, rows?: number): void {
  void request('POST', '/exports', { what, scope, rows }).catch(() => undefined)
}

export function fetchList<T>(resource: string, query: ListQuery, opts?: RequestOptions): Promise<Page<T>> {
  return request<Page<T>>('GET', `/${resource}?${toSearchParams(query).toString()}`, undefined, opts)
}

export function fetchItem<T>(resource: string, id: string, opts?: RequestOptions): Promise<T> {
  return request<T>('GET', `/${resource}/${encodeURIComponent(id)}`, undefined, opts)
}

export interface FacetBucket {
  value: string
  count: number
}

/** Счётчики по значениям колонок. Считает сервер: клиент видит только страницу. */
export function fetchFacets(
  resource: string,
  by: readonly string[],
  opts?: RequestOptions,
): Promise<Record<string, FacetBucket[]>> {
  return request<Record<string, FacetBucket[]>>(
    'GET',
    `/facets/${resource}?by=${encodeURIComponent(by.join(','))}`,
    undefined,
    opts,
  )
}

export function fetchMeta<T>(name: string, opts?: RequestOptions): Promise<T> {
  return request<T>('GET', `/meta/${name}`, undefined, opts)
}

export function fetchSub<T>(path: string, opts?: RequestOptions): Promise<T> {
  return request<T>('GET', path, undefined, opts)
}
